import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, MessageSquare, Plus, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { parseUtcDate } from '@/lib/datetime';
import type { ConversationSummary } from '@/lib/api-client';
import { useDismiss } from '@/hooks/ui/useDismiss';

/**
 * Compact dropdown for picking a chat thread + starting a new one.
 *
 * Lives in the chat panel's heading. Click the title row to open the
 * popover; the popover lists every conversation in this lesson with
 * "+ New chat" pinned at the top.
 *
 * Visual mirrors the indigo-themed VersionSelector / FlowSelector pattern
 * elsewhere in the lesson UI: rounded-xl items, `bg-brand-50/70` wash on
 * the active row, indigo Check icon. The delete affordance is tucked
 * behind a hover ⋯ menu so untouched rows read clean.
 *
 * Pure presentational - the parent owns the conversation list, the
 * active id, and all CRUD handlers.
 */

interface ConversationSelectorProps {
  conversations: ConversationSummary[];
  activeConversation: ConversationSummary | undefined;
  onSelect: (conversationId: string) => void;
  onNewChat: () => void;
  onDelete: (conversationId: string) => void;
  /** Optional outer wrapper className. */
  className?: string;
}

/** Untitled fallback for conversations whose title hasn't been set yet
 *  (no first message). Kept distinct from the "+ New chat" action label
 *  above so the two don't collide visually. */
const UNTITLED_LABEL = 'Untitled';

export function ConversationSelector({
  conversations,
  activeConversation,
  onSelect,
  onNewChat,
  onDelete,
  className = '',
}: ConversationSelectorProps) {
  const [open, setOpen] = useState(false);
  // Index of the currently focused item in the popover (for arrow-key
  // navigation). -1 means nothing is focused yet.
  const [focusedIdx, setFocusedIdx] = useState<number>(-1);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside-press or Escape. `useDismiss` stops the Escape, so in
  // the phone's chat sheet it closes this list and not the sheet too.
  useDismiss(ref, open, () => setOpen(false));

  // Reset arrow-key focus whenever the popover opens.
  useEffect(() => {
    if (open) setFocusedIdx(-1);
  }, [open]);

  const handlePick = (id: string) => {
    onSelect(id);
    setOpen(false);
  };

  const handleNewClick = () => {
    onNewChat();
    setOpen(false);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onDelete(id);
  };

  // Don't show the count badge until at least one conversation has a
  // real message in it. The bootstrap effect in DoubtsExplorePanel
  // eagerly creates an empty conversation if none exist, which would
  // otherwise show "(1)" on a freshly-opened panel that the user
  // perceives as having no chat yet.
  const meaningfulCount = useMemo(
    () => conversations.filter(c => (c.message_count ?? 0) > 0).length,
    [conversations]
  );

  // Trigger label resolution:
  //   1. Active conversation has a title              → that title
  //   2. Active conversation is empty (no messages)    → "Chat" (panel name)
  //   3. No active conversation                        → "Chat"
  // We deliberately avoid the literal string "New chat" here because it
  // collides with the "+ New chat" action inside the popover.
  const activeHasMessages = (activeConversation?.message_count ?? 0) > 0;
  const triggerLabel = activeConversation?.title
    ? activeConversation.title
    : activeHasMessages
      ? UNTITLED_LABEL
      : 'Chat';

  // Keyboard nav for the popover list.
  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIdx(i => Math.min(i + 1, conversations.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && focusedIdx >= 0) {
      e.preventDefault();
      const conv = conversations[focusedIdx];
      if (conv) handlePick(conv.conversation_id);
    }
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        // Opening the list leaves focus HERE, not in the popover - so a
        // handler on the popover alone never saw the arrow keys, and keyboard
        // navigation (#127) did nothing until the user tabbed in. Enter only
        // picks once a row is highlighted; otherwise it is the button's own
        // toggle.
        onKeyDown={handleListKeyDown}
        aria-haspopup='listbox'
        aria-expanded={open}
        className={cn(
          // `tap-target`: drawn 32px, tapped as 44px - in the phone chat sheet
          // this is the only way to another thread.
          'tap-target group flex h-8 items-center gap-2 rounded-md px-2.5 text-base font-medium transition-colors',
          'text-foreground hover:bg-muted',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 focus-visible:ring-offset-0 dark:focus-visible:ring-primary/60',
          open && 'bg-muted'
        )}
      >
        <MessageSquare className='h-4 w-4 text-brand-500/80 dark:text-primary' />
        <span className='max-w-[180px] truncate'>{triggerLabel}</span>
        {meaningfulCount > 0 && (
          <span className='dark:tint-brand rounded-full bg-brand-100 px-1.5 py-0.5 text-xs font-medium text-brand-700'>
            {meaningfulCount}
          </span>
        )}
        <ChevronDown
          className={cn(
            'h-3 w-3 text-muted-foreground transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div
          // `group`, NOT `listbox`. Two things were wrong with the listbox:
          //
          //  * `aria-required-children` — a listbox must own `option`s, and
          //    this one wraps its rows in layout divs and pins a "+ New chat"
          //    ACTION at the top, which is not an option in any sense.
          //  * `nested-interactive` — each row was `role='option'` containing
          //    a focusable delete button. An option that contains a button is
          //    something assistive tech cannot present coherently.
          //
          // The same call `TagFilter` and the notes selector already took in
          // this repo: a labelled group of ordinary buttons, with `aria-current`
          // marking the active row. Arrow-key navigation is kept — it is real
          // and useful — it just no longer claims listbox semantics it does not
          // implement (no `aria-activedescendant`, no roving focus).
          // No role on the POPOVER itself. It holds an action ("+ New chat")
          // and a list, which no single role describes — and giving it a
          // non-interactive role while it carries a keydown handler is its own
          // lint error. The group goes on the list below, where it is true.
          tabIndex={-1}
          onKeyDown={handleListKeyDown}
          className='absolute left-0 top-10 z-50 w-72 overflow-hidden rounded-2xl border border-border bg-card shadow-lg'
        >
          <div className='p-1.5'>
            <button
              type='button'
              onClick={handleNewClick}
              className='flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted dark:hover:bg-accent'
            >
              <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:bg-muted dark:text-primary'>
                <Plus className='h-3.5 w-3.5' />
              </span>
              <span>New chat</span>
            </button>

            {conversations.length > 0 && (
              <div className='my-1 border-t border-border' />
            )}

            {conversations.length === 0 ? (
              <div className='px-3 py-3 text-center text-xs text-muted-foreground'>
                No chats yet.
              </div>
            ) : (
              // The group is HERE — around the conversations only. "+ New
              // chat" above is an action, not a conversation, and including it
              // is what made the old `listbox` claim wrong in the first place.
              <div
                role='group'
                aria-label='Conversations'
                className='max-h-72 space-y-1 overflow-y-auto'
              >
                {conversations.map((conv, idx) => {
                  const isActive =
                    conv.conversation_id ===
                    activeConversation?.conversation_id;
                  const isFocused = idx === focusedIdx;
                  const hasMessages = (conv.message_count ?? 0) > 0;
                  // Always fall back to "Untitled" for a row that has no
                  // server-assigned title - using "New chat" here would
                  // collide with the "+ New chat" action button above.
                  const displayTitle = conv.title || UNTITLED_LABEL;
                  // Relative time - defensive parse so naive UTC timestamps
                  // don't display in local time accidentally.
                  const updatedAt =
                    conv.updated_at && parseUtcDate(conv.updated_at);
                  const timeAgo = updatedAt
                    ? formatDistanceToNow(updatedAt, { addSuffix: true })
                    : null;
                  return (
                    <div
                      key={conv.conversation_id}
                      className={cn(
                        'group/row relative flex w-full items-start gap-2 rounded-xl px-3 py-2.5 transition-colors',
                        isActive
                          ? 'bg-brand-50/70 dark:bg-tint-brand'
                          : isFocused
                            ? 'bg-muted dark:bg-accent'
                            : 'hover:bg-muted dark:hover:bg-accent'
                      )}
                    >
                      <button
                        type='button'
                        // On the SELECT control, not the row wrapper — the row
                        // is now an inert container holding two buttons.
                        aria-current={isActive}
                        onClick={() => handlePick(conv.conversation_id)}
                        className='flex flex-1 items-start gap-2 overflow-hidden text-left max-sm:min-h-11 max-sm:items-center'
                      >
                        <MessageSquare
                          className={cn(
                            'mt-0.5 h-4 w-4 flex-shrink-0',
                            isActive
                              ? 'text-brand-600 dark:text-primary'
                              : 'text-muted-foreground'
                          )}
                        />
                        <div className='min-w-0 flex-1'>
                          <div
                            className={cn(
                              'truncate text-sm',
                              isActive
                                ? 'font-medium text-brand-700 dark:text-tint-brand-foreground'
                                : 'text-foreground'
                            )}
                          >
                            {displayTitle}
                          </div>
                          <div className='mt-0.5 truncate text-xs text-muted-foreground'>
                            {hasMessages
                              ? `${conv.message_count} message${conv.message_count === 1 ? '' : 's'}`
                              : 'No messages yet'}
                            {timeAgo ? ` · ${timeAgo}` : ''}
                          </div>
                        </div>
                      </button>

                      {/* Trailing delete affordance - hover-revealed with a
                          mouse, always shown on a phone or touch screen
                          (no hover there), with a 44px tap area. On every
                          row (including the active one, since the
                          parent's delete handler falls through to the
                          next conversation when the active one is
                          removed). The indigo wash + indigo-tinted title
                          already signal which row is current; we don't
                          need a separate Check icon. */}
                      <button
                        type='button'
                        onClick={e =>
                          handleDeleteClick(e, conv.conversation_id)
                        }
                        // Named per conversation. The panel renders one of
                        // these per row, and a screen reader's element list of
                        // identical "Delete conversation" buttons is unusable.
                        aria-label={`Delete ${conv.title || 'conversation'}`}
                        title='Delete conversation'
                        className='tap-target flex-shrink-0 rounded-sm p-1 text-muted-foreground opacity-0 transition-all hover:bg-rose-50 hover:text-rose-600 focus-visible:opacity-100 group-hover/row:opacity-100 dark:hover:bg-rose-900/20 dark:hover:text-rose-400 max-sm:self-center max-sm:opacity-100 [@media(pointer:coarse)]:opacity-100'
                      >
                        <Trash2 className='h-3.5 w-3.5' />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
